/*
 * © 2026 eggs unimedia GmbH
 * Coded with love 💛
 */

const DM_PARAMS = { dpr: 'on,1', fmt: 'webp', qlt: '85' };

/**
 * Normalize a Dynamic Media S7 URL — spaces in asset names become underscores in DM.
 */
const normalizeDmUrl = (url) => {
  if (!url) return url;
  // Replace encoded spaces (%20) and literal spaces with underscores
  return url.replace(/%20/g, '_').replace(/ /g, '_');
};

export const fetchSmartCrops = async (dmS7Url) => {
  if (!dmS7Url?.includes('scene7.com')) return null;
  const url = normalizeDmUrl(dmS7Url);
  
  try {
    const res = await fetch(`${url}?req=set,json`);
    const text = await res.text();
    const json = JSON.parse(text.match(/\{.*\}/s)?.[0] || '{}');
    return json.set?.relation?.map(r => r.n.split(':')[1]) || [];
  } catch {
    return null;
  }
};

export const getSmartCropUrl = (dmS7Url, cropName, params = {}) => {
  if (!dmS7Url?.includes('scene7.com')) return null;
  const url = normalizeDmUrl(dmS7Url);
  
  const merged = { ...DM_PARAMS, ...params };
  const query = Object.entries(merged).map(([k, v]) => `${k}=${v}`).join('&');
  
  return `${url}:${cropName}?${query}`;
};

export const getDynamicMediaUrl = (imageObj, cropName = null, params = {}) => {
  const dmUrl = normalizeDmUrl(imageObj?._dmS7Url);
  if (!dmUrl) return imageObj?._publishUrl || imageObj?._path || null;
  
  if (cropName) return getSmartCropUrl(dmUrl, cropName, params);
  
  const merged = { ...DM_PARAMS, ...params };
  const query = Object.entries(merged).map(([k, v]) => `${k}=${v}`).join('&');
  return `${dmUrl}?${query}`;
};

/**
 * Get the best image URL for an AEM image object, using SmartCrops when available.
 *
 * @param {Object} imageObj - AEM image reference (_dmS7Url, _publishUrl, _dynamicUrl)
 * @param {string[]} [smartCrops] - Available SmartCrop names from the speaker/gondola provider
 * @param {string} [preferredCrop] - Preferred crop name (e.g. 'Small' for avatars, 'Large' for hero)
 * @returns {{ src: string, sources?: { media: string, srcSet: string }[] } | null}
 */
export const getImageUrl = (imageObj, smartCrops = [], preferredCrop = null) => {
  if (!imageObj) return null

  // Try SmartCrop first (requires _dmS7Url)
  if (imageObj._dmS7Url && smartCrops?.length) {
    const fallback = getDynamicMediaUrl(imageObj, null)
    // Single preferred crop (e.g. avatar)
    if (preferredCrop && smartCrops.includes(preferredCrop)) {
      return { src: getDynamicMediaUrl(imageObj, preferredCrop) }
    }
    // Responsive sources for hero images
    const sources = []
    if (smartCrops.includes('Small')) {
      sources.push({ media: '(max-width: 639px)', srcSet: getDynamicMediaUrl(imageObj, 'Small') })
    }
    if (smartCrops.includes('Small')) {
      sources.push({ media: '(min-width: 640px) and (max-width: 1023px)', srcSet: getDynamicMediaUrl(imageObj, 'Small') })
    }
    if (smartCrops.includes('Medium')) {
      sources.push({ media: '(min-width: 1024px)', srcSet: getDynamicMediaUrl(imageObj, 'Medium') })
    }
    return { src: fallback, sources: sources.length ? sources : undefined }
  }

  // _dmS7Url without crops
  if (imageObj._dmS7Url) return { src: getDynamicMediaUrl(imageObj) }
  // _publishUrl is already absolute
  if (imageObj._publishUrl) return { src: imageObj._publishUrl }
  // _dynamicUrl is relative — prefix with AEM host
  if (imageObj._dynamicUrl) {
    const endpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT || 'http://localhost:4502/graphql/execute.json/ewsr'
    return { src: `${new URL(endpoint).origin}${imageObj._dynamicUrl}` }
  }
  return null
}

/**
 * Get absolute URL for DAM assets (materials, documents, etc.)
 * Prefers _publishUrl, falls back to constructing from _path
 */
export const getAssetUrl = (assetObj) => {
  // If we have _publishUrl, use it directly
  if (assetObj?._publishUrl) {
    return assetObj._publishUrl;
  }
  
  // If we have _path, construct absolute URL using GraphQL endpoint host
  if (assetObj?._path) {
    const graphqlEndpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT || 'http://localhost:4502/graphql/execute.json/ewsr';
    const baseUrl = new URL(graphqlEndpoint).origin;
    return `${baseUrl}${assetObj._path}`;
  }
  
  // Fallback to string if passed directly
  if (typeof assetObj === 'string') {
    // If already absolute, return as-is
    if (assetObj.startsWith('http://') || assetObj.startsWith('https://')) {
      return assetObj;
    }
    // Otherwise construct absolute URL
    const graphqlEndpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT || 'http://localhost:4502/graphql/execute.json/ewsr';
    const baseUrl = new URL(graphqlEndpoint).origin;
    return `${baseUrl}${assetObj}`;
  }
  
  return null;
};
